import type { Directive, DirectiveBinding } from 'vue';
import { usePermissionStore } from '@/stores/usePermissionStore';

/**
 * v-can directive for permission-based conditional rendering
 *
 * Usage:
 * - v-can="'permission.name'" - Show if user has this permission
 * - v-can="['perm1', 'perm2']" - Show if user has ANY of these permissions
 * - v-can.all="['perm1', 'perm2']" - Show if user has ALL of these permissions
 *
 * Examples:
 * <button v-can="'mailbox.create'">Create Mailbox</button>
 * <div v-can="['user.view', 'user.edit']">User Actions</div>
 * <div v-can.all="['user.view', 'user.edit']">Edit User (needs both)</div>
 */
export const canDirective: Directive = {
  mounted(el: HTMLElement, binding: DirectiveBinding) {
    checkPermissionAndToggle(el, binding);
  },
  updated(el: HTMLElement, binding: DirectiveBinding) {
    checkPermissionAndToggle(el, binding);
  }
};

function checkPermissionAndToggle(el: HTMLElement, binding: DirectiveBinding) {
  const permissionStore = usePermissionStore();
  const permissions = binding.value;
  const requireAll = binding.modifiers.all;

  let hasAccess = false;

  if (typeof permissions === 'string') {
    // Single permission string
    hasAccess = permissionStore.hasPermission(permissions);
  } else if (Array.isArray(permissions)) {
    // Array of permissions
    if (requireAll) {
      // User must have ALL permissions
      hasAccess = permissionStore.hasAllPermissions(permissions);
    } else {
      // User must have ANY permission (default)
      hasAccess = permissionStore.hasAnyPermission(permissions);
    }
  }

  // Hide/show element based on permission
  if (!hasAccess) {
    el.style.display = 'none';
  } else {
    el.style.display = '';
  }
}
